import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { locations } from '../mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message sent! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-black mb-4">Contact Us</h1>
          <p className="text-xl text-gray-900">Get in touch with us for any queries</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">Send us a Message</h2>
            <Card className="bg-gray-900 border-yellow-500/20">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-white mb-2 font-semibold">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-black border border-yellow-500/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-yellow-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white mb-2 font-semibold">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-black border border-yellow-500/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-yellow-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white mb-2 font-semibold">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-black border border-yellow-500/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-yellow-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white mb-2 font-semibold">Message</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows="4"
                      className="w-full bg-black border border-yellow-500/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-yellow-400"
                      required
                    ></textarea>
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold">
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">Get in Touch</h2>
            
            <div className="space-y-6 mb-8">
              <Card className="bg-gray-900 border-yellow-500/20">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-yellow-400/10 rounded-full flex items-center justify-center mr-4">
                      <Mail className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Email</h3>
                      <p className="text-gray-400">info@thesportbloc.com</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900 border-yellow-500/20">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-yellow-400/10 rounded-full flex items-center justify-center mr-4">
                      <Phone className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Phone</h3>
                      <p className="text-gray-400">+91 98765 43210</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-900 border-yellow-500/20">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-yellow-400/10 rounded-full flex items-center justify-center mr-4">
                      <MapPin className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Address</h3>
                      <p className="text-gray-400">Shillong, Meghalaya, India</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Location Cards */}
            <h3 className="text-2xl font-bold text-white mb-4">Our Locations</h3>
            <div className="space-y-4">
              {locations.map((location) => (
                <Card key={location.id} className="bg-gray-900 border-yellow-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-white font-bold text-lg">{location.name}</h4>
                        <p className="text-yellow-400 text-sm mb-2">{location.sport}</p>
                        <p className="text-gray-400 text-sm flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {location.address}
                        </p>
                        <p className="text-gray-400 text-sm flex items-center mt-1">
                          <Phone className="w-4 h-4 mr-1" />
                          {location.contact}
                        </p>
                      </div>
                      <div className="text-3xl">
                        {location.sportIcon.startsWith('/') ? (
                          <img src={location.sportIcon} alt={location.sport} className="w-10 h-10" />
                        ) : (
                          location.sportIcon
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;